import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  getLoanDataExportFilterOptions,
  resolveLoanDataExportNumbers,
  searchLoanDataExportCandidates
} from '@/services/loanDataExportService';
import {
  addLoanDataExportCandidates,
  LOAN_DATA_EXPORT_MAX_SELECTION,
  removeLoanDataExportCandidate
} from '@/stores/loanDataExportSelectionStore';
import type {
  LoanDataExportCandidate,
  LoanDataExportCandidateFilters,
  LoanDataExportFilterOptions
} from '@/types/loan-data-export';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { formatCurrency } from '@/utils/formatCurrency';
import { LoanCohortFilters } from './LoanCohortFilters';
import {
  buildLoanDataExportCandidateSearchInput,
  createDefaultLoanDataExportFilters,
  getLoanDataExportCandidateDate,
  loanDataExportDateFieldLabel
} from './loanDataExportFilterModel';

interface LoanSelectionPanelProps {
  /** Selected loans that belong to the signed-in user and passed the access check. */
  selected: LoanDataExportCandidate[];
  /** Owner of the shared selection; every add is checked against it. */
  ownerKey: string | null;
  /** The selection menu and Generate Excel, shown in the table toolbar. */
  toolbarActions?: ReactNode;
}

const PAGE_SIZE = 100;
const LIMIT_MESSAGE = 'Exports are limited to 1,000 loans, and the selection is full';

function pluralized(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

function borrowerName(candidate: LoanDataExportCandidate): string {
  return [candidate.borrowerFirstName, candidate.borrowerLastName].filter(Boolean).join(' ') || 'Unknown borrower';
}

function formatTableDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = parseAsLocalDate(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

export function LoanSelectionPanel({ selected, ownerKey, toolbarActions }: LoanSelectionPanelProps) {
  const [filters, setFilters] = useState<LoanDataExportCandidateFilters>(createDefaultLoanDataExportFilters);
  const [filterOptions, setFilterOptions] = useState<LoanDataExportFilterOptions | null>(null);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<LoanDataExportCandidate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingAll, setAddingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSequence = useRef(0);
  const filtersRef = useRef(filters);
  const mountedRef = useRef(true);

  const [numbersOpen, setNumbersOpen] = useState(false);
  const [bulkLoanNumbers, setBulkLoanNumbers] = useState('');
  const [resolving, setResolving] = useState(false);
  const [unresolved, setUnresolved] = useState<string[]>([]);

  const selectedIds = useMemo(() => new Set(selected.map((candidate) => candidate.id)), [selected]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const allPageSelected = items.length > 0 && items.every((candidate) => selectedIds.has(candidate.id));
  const selectionFull = selected.length >= LOAN_DATA_EXPORT_MAX_SELECTION;
  const dateLabel = loanDataExportDateFieldLabel(filters.dateField).replace(' date', '');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      setFilterOptionsLoading(true);
      setFilterOptionsError(null);
      try {
        const nextOptions = await getLoanDataExportFilterOptions();
        if (!cancelled) {
          setFilterOptions(nextOptions);
        }
      } catch (optionsError) {
        if (!cancelled) {
          setFilterOptionsError(optionsError instanceof Error
            ? optionsError.message
            : 'Unable to load loan filter options');
        }
      } finally {
        if (!cancelled) {
          setFilterOptionsLoading(false);
        }
      }
    };
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await searchLoanDataExportCandidates(
          buildLoanDataExportCandidateSearchInput(filters, page, PAGE_SIZE)
        );
        if (requestSequence.current === sequence) {
          setItems(result.items);
          setTotalCount(result.totalCount);
        }
      } catch (requestError) {
        if (requestSequence.current === sequence) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to search loans');
          setItems([]);
          setTotalCount(0);
        }
      } finally {
        if (requestSequence.current === sequence) {
          setLoading(false);
        }
      }
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      requestSequence.current += 1;
    };
  }, [filters, page]);

  const addCandidates = (candidates: LoanDataExportCandidate[], owner: string | null, announce: boolean) => {
    const result = addLoanDataExportCandidates(candidates, owner);
    if (result.added > 0 && announce) {
      toast.success(`${pluralized(result.added, 'loan')} selected`);
    }
    if (result.skipped > 0 && result.atLimit) {
      toast.info(LIMIT_MESSAGE);
    } else if (announce && result.added === 0 && result.skipped > 0) {
      toast.info('Those loans are already selected');
    }
  };

  const handleResolve = async () => {
    const loanNumbers = Array.from(new Set(
      bulkLoanNumbers.split(/[\s,;]+/).map((number) => number.trim()).filter(Boolean)
    ));
    if (loanNumbers.length === 0) {
      toast.error('Enter at least one loan number');
      return;
    }
    if (loanNumbers.length > LOAN_DATA_EXPORT_MAX_SELECTION) {
      toast.error('A maximum of 1,000 loan numbers can be added at once');
      return;
    }

    const owner = ownerKey;
    setResolving(true);
    setUnresolved([]);
    try {
      const resolved = await resolveLoanDataExportNumbers(loanNumbers);
      if (!mountedRef.current) {
        return;
      }
      const found = new Set(resolved.map((candidate) => candidate.loanNumber.toLowerCase()));
      const missing = loanNumbers.filter((number) => !found.has(number.toLowerCase()));
      setUnresolved(missing);
      addCandidates(resolved, owner, true);
      if (missing.length === 0 && resolved.length > 0) {
        setBulkLoanNumbers('');
        setNumbersOpen(false);
      }
    } catch (resolveError) {
      if (mountedRef.current) {
        toast.error(resolveError instanceof Error ? resolveError.message : 'Unable to look up loan numbers');
      }
    } finally {
      if (mountedRef.current) {
        setResolving(false);
      }
    }
  };

  // The displayed count can trail a filter change by one search, so the
  // first page is fetched again here for a current total. Results count only
  // if the filters and the panel are unchanged when they arrive.
  const handleSelectAllMatching = async () => {
    const startFilters = filters;
    const owner = ownerKey;
    setAddingAll(true);
    try {
      const firstPage = await searchLoanDataExportCandidates(
        buildLoanDataExportCandidateSearchInput(startFilters, 1, PAGE_SIZE)
      );
      const limit = Math.min(firstPage.totalCount, LOAN_DATA_EXPORT_MAX_SELECTION);
      const pageCount = Math.ceil(limit / PAGE_SIZE);
      const remaining = pageCount > 1
        ? await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => searchLoanDataExportCandidates(
          buildLoanDataExportCandidateSearchInput(startFilters, index + 2, PAGE_SIZE)
        )))
        : [];
      if (!mountedRef.current || filtersRef.current !== startFilters) {
        return;
      }
      const matching = [firstPage, ...remaining]
        .flatMap((result) => result.items)
        .slice(0, LOAN_DATA_EXPORT_MAX_SELECTION);
      addCandidates(matching, owner, true);
      if (firstPage.totalCount > LOAN_DATA_EXPORT_MAX_SELECTION) {
        toast.info('Only the first 1,000 matching loans were selected, because exports are limited to 1,000 loans');
      }
    } catch (addError) {
      if (mountedRef.current) {
        toast.error(addError instanceof Error ? addError.message : 'Unable to select the matching loans');
      }
    } finally {
      if (mountedRef.current) {
        setAddingAll(false);
      }
    }
  };

  const handleTogglePage = (checked: boolean) => {
    if (checked) {
      addCandidates(items, ownerKey, false);
      return;
    }
    items.forEach((candidate) => removeLoanDataExportCandidate(candidate.id));
  };

  const allMatchingSelected = !loading && totalCount > 0 && totalPages === 1 && allPageSelected;
  const selectAllCount = Math.min(totalCount, LOAN_DATA_EXPORT_MAX_SELECTION);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <LoanCohortFilters
        filters={filters}
        options={filterOptions}
        loadingOptions={filterOptionsLoading}
        optionsError={filterOptionsError}
        onChange={setFilters}
        onPasteLoanNumbers={() => {
          setUnresolved([]);
          setNumbersOpen(true);
        }}
      />

      <section className="min-w-0 overflow-hidden rounded-xl border bg-card" aria-label="Loans">
        <div className="flex flex-col gap-3 border-b px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <p className="text-muted-foreground" aria-live="polite">
              {loading ? 'Searching…' : (
                <>
                  <span className="font-semibold tabular-nums text-foreground">{totalCount.toLocaleString()}</span>
                  {filterOptions
                    ? ` of ${filterOptions.totalEligible.toLocaleString()} loans match`
                    : ` ${totalCount === 1 ? 'loan matches' : 'loans match'}`}
                </>
              )}
            </p>
            {allMatchingSelected ? (
              <p className="text-muted-foreground">All {totalCount.toLocaleString()} are selected</p>
            ) : (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-sm font-semibold"
                disabled={loading || totalCount === 0 || selectionFull || addingAll}
                onClick={() => void handleSelectAllMatching()}
              >
                {addingAll ? <Loader2 className="animate-spin" /> : null}
                Select all {selectAllCount.toLocaleString()} matching
              </Button>
            )}
          </div>
          {toolbarActions ? (
            <div className="flex flex-wrap items-center gap-2">{toolbarActions}</div>
          ) : null}
        </div>

        <Table
          aria-label="Export-eligible loans"
          className="min-w-[860px] table-fixed text-sm [line-height:normal]"
          containerProps={{ className: 'overflow-x-auto' }}
        >
          <colgroup>
            <col className="w-12" />
            <col className="w-[23%]" />
            <col className="w-[21%]" />
            <col className="w-[25%]" />
            <col className="w-[9%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
          </colgroup>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 pl-4 pr-0">
                <Checkbox
                  checked={allPageSelected}
                  className="h-[18px] w-[18px] rounded-[4px] border-input shadow-none"
                  aria-label={allPageSelected ? 'Clear the loans on this page' : 'Select the loans on this page'}
                  disabled={items.length === 0 || (!allPageSelected && selectionFull)}
                  onCheckedChange={(checked) => handleTogglePage(checked === true)}
                />
              </TableHead>
              <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">Loan</TableHead>
              <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">Product</TableHead>
              <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">Investor and program</TableHead>
              <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">AUS</TableHead>
              <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">{dateLabel}</TableHead>
              <TableHead className="h-10 pl-3 pr-4 text-right text-xs font-medium text-muted-foreground">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-40 text-center text-muted-foreground">
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading loans…
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="h-40 text-center text-destructive">{error}</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-40 text-center text-muted-foreground">
                  No loans match these filters.
                </TableCell>
              </TableRow>
            ) : items.map((candidate) => {
              const checked = selectedIds.has(candidate.id);
              return (
                <TableRow
                  key={candidate.id}
                  className="hover:bg-muted/40 data-[state=selected]:bg-primary/[0.06]"
                  data-state={checked ? 'selected' : undefined}
                >
                  <TableCell className="py-3 pl-4 pr-0 align-top">
                    <Checkbox
                      checked={checked}
                      className="h-[18px] w-[18px] rounded-[4px] border-input shadow-none"
                      aria-label={`Select ${candidate.loanNumber || 'loan'}`}
                      disabled={!checked && selectionFull}
                      onCheckedChange={(nextChecked) => {
                        if (nextChecked === true) {
                          addCandidates([candidate], ownerKey, false);
                        } else {
                          removeLoanDataExportCandidate(candidate.id);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="px-3 py-3 align-top">
                    <div className="truncate font-medium tabular-nums">{candidate.loanNumber || '—'}</div>
                    <div className="truncate text-xs text-muted-foreground">{borrowerName(candidate)}</div>
                  </TableCell>
                  <TableCell className="px-3 py-3 align-top">
                    <div className="truncate" title={candidate.productCategory || undefined}>{candidate.productCategory || '—'}</div>
                    <div className="truncate text-xs text-muted-foreground">{candidate.loanPurpose || '—'}</div>
                  </TableCell>
                  <TableCell className="px-3 py-3 align-top">
                    <div className="truncate" title={candidate.investorName || undefined}>{candidate.investorName || '—'}</div>
                    <div className="truncate text-xs text-muted-foreground" title={candidate.loanProgram || undefined}>{candidate.loanProgram || '—'}</div>
                  </TableCell>
                  <TableCell className="px-3 py-3 align-top">{candidate.ausDecision || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap px-3 py-3 align-top">
                    {formatTableDate(getLoanDataExportCandidateDate(candidate, filters.dateField))}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-3 pl-3 pr-4 text-right align-top tabular-nums">
                    {formatCurrency(candidate.loanAmount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {totalPages > 1 ? (
          <div className="flex items-center justify-between gap-3 border-t px-4 py-2.5">
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>
                Previous
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <Dialog open={numbersOpen} onOpenChange={setNumbersOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Paste loan numbers</DialogTitle>
            <DialogDescription>
              Add up to 1,000 loans. Separate loan numbers with spaces, commas, semicolons, or new lines.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label htmlFor="bulk-loan-numbers" className="text-sm font-medium">Loan numbers</label>
            <Textarea
              id="bulk-loan-numbers"
              value={bulkLoanNumbers}
              onChange={(event) => setBulkLoanNumbers(event.target.value)}
              placeholder={'LW26010001\nLW26010002\nLW26010003'}
              className="min-h-40 font-mono"
            />
            {unresolved.length > 0 ? (
              <div role="alert" className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium">
                  <AlertCircle className="h-4 w-4" />
                  {unresolved.length} not found or not exportable
                </div>
                <p className="mt-1 break-words font-mono text-xs text-muted-foreground">
                  {unresolved.slice(0, 25).join(', ')}{unresolved.length > 25 ? ` and ${unresolved.length - 25} more` : ''}
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setNumbersOpen(false)}>Cancel</Button>
            <Button type="button" disabled={resolving} onClick={() => void handleResolve()}>
              {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add loans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
